/**
 * DataTable Component
 * გადატანადი ცხრილის კომპონენტი
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { TableSkeleton } from '../Loading';
import { Button } from '../Button';

export interface TableColumn<T> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: unknown, row: T, index: number) => React.ReactNode;
}

export interface TableAction<T> {
  label: string;
  icon?: React.ReactNode;
  onClick: (row: T) => void;
  variant?: 'default' | 'danger' | 'primary';
  show?: (row: T) => boolean;
}

export interface DataTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  actions?: TableAction<T>[];
  isLoading?: boolean;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;
  onRowClick?: (row: T) => void;
  rowKey?: keyof T | ((row: T) => string);
  sortable?: boolean;
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  currentSort?: { key: string; direction: 'asc' | 'desc' };
  striped?: boolean;
  hoverable?: boolean;
  compact?: boolean;
  stickyHeader?: boolean;
  className?: string;
}

export function DataTable<T extends Record<string, unknown>>({
  data,
  columns,
  actions,
  isLoading = false,
  emptyMessage = 'No data available',
  emptyIcon,
  onRowClick,
  rowKey = 'id' as keyof T,
  sortable = false,
  onSort,
  currentSort,
  striped = false,
  hoverable = true,
  compact = false,
  stickyHeader = false,
  className,
}: DataTableProps<T>) {
  const [localSort, setLocalSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  const activeSort = currentSort || localSort;

  const getRowKey = (row: T, index: number): string => {
    if (typeof rowKey === 'function') {
      return rowKey(row);
    }
    return String(row[rowKey] ?? index);
  };

  const getValue = (row: T, key: keyof T | string): unknown => {
    const keys = String(key).split('.');
    let value: unknown = row;
    for (const k of keys) {
      value = (value as Record<string, unknown>)?.[k];
    }
    return value;
  };

  const handleSort = (key: string) => {
    if (!sortable) return;

    const newDirection =
      activeSort?.key === key && activeSort.direction === 'asc' ? 'desc' : 'asc';

    if (onSort) {
      onSort(key, newDirection);
    } else {
      setLocalSort({ key, direction: newDirection });
    }
  };

  const sortedData = React.useMemo(() => {
    if (!activeSort || onSort) return data;

    return [...data].sort((a, b) => {
      const aVal = getValue(a, activeSort.key);
      const bVal = getValue(b, activeSort.key);

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison = aVal < bVal ? -1 : 1;
      return activeSort.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, activeSort, onSort]);

  if (isLoading) {
    return (
      <div className={clsx('bg-white rounded-lg border border-gray-200 p-4', className)}>
        <TableSkeleton rows={5} columns={columns.length} />
      </div>
    );
  }

  return (
    <div className={clsx('bg-white rounded-lg border border-gray-200 overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className={clsx('bg-gray-50', stickyHeader && 'sticky top-0 z-10')}>
            <tr>
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  scope="col"
                  style={{ width: column.width }}
                  className={clsx(
                    'text-xs font-semibold text-gray-600 uppercase tracking-wider',
                    compact ? 'px-4 py-2' : 'px-6 py-3',
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right',
                    column.align !== 'center' && column.align !== 'right' && 'text-left',
                    sortable && column.sortable !== false && 'cursor-pointer hover:bg-gray-100'
                  )}
                  onClick={() => column.sortable !== false && handleSort(String(column.key))}
                >
                  <div className="flex items-center gap-1">
                    {column.header}
                    {sortable && column.sortable !== false && (
                      <span className="text-gray-400">
                        {activeSort?.key === String(column.key) ? (
                          activeSort.direction === 'asc' ? '↑' : '↓'
                        ) : (
                          '↕'
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              {actions && actions.length > 0 && (
                <th
                  scope="col"
                  className={clsx(
                    'text-xs font-semibold text-gray-600 uppercase tracking-wider text-right',
                    compact ? 'px-4 py-2' : 'px-6 py-3'
                  )}
                >
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (actions ? 1 : 0)}
                  className="px-6 py-12 text-center"
                >
                  <div className="flex flex-col items-center">
                    {emptyIcon && <div className="text-gray-400 mb-3">{emptyIcon}</div>}
                    <p className="text-gray-500">{emptyMessage}</p>
                  </div>
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => (
                <tr
                  key={getRowKey(row, index)}
                  className={clsx(
                    striped && index % 2 === 1 && 'bg-gray-50',
                    hoverable && 'hover:bg-gray-50',
                    onRowClick && 'cursor-pointer'
                  )}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((column) => (
                    <td
                      key={String(column.key)}
                      className={clsx(
                        'text-sm text-gray-900 whitespace-nowrap',
                        compact ? 'px-4 py-2' : 'px-6 py-4',
                        column.align === 'center' && 'text-center',
                        column.align === 'right' && 'text-right'
                      )}
                    >
                      {column.render
                        ? column.render(getValue(row, column.key), row, index)
                        : String(getValue(row, column.key) ?? '-')}
                    </td>
                  ))}
                  {actions && actions.length > 0 && (
                    <td
                      className={clsx(
                        'text-sm text-right whitespace-nowrap',
                        compact ? 'px-4 py-2' : 'px-6 py-4'
                      )}
                    >
                      <div className="flex justify-end gap-2">
                        {actions
                          .filter((action) => !action.show || action.show(row))
                          .map((action, i) => (
                            <Button
                              key={i}
                              size="xs"
                              variant={
                                action.variant === 'danger'
                                  ? 'danger'
                                  : action.variant === 'primary'
                                  ? 'primary'
                                  : 'ghost'
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                action.onClick(row);
                              }}
                              leftIcon={action.icon}
                            >
                              {action.label}
                            </Button>
                          ))}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
